import { useEffect, useState } from 'react'
import axios from 'axios'
import { Review } from '~/components/Reviews/reviewTypes'

/**
 * Response shape for GET /api/reviews/google (backend reviews module).
 * Mirrors the Google Business Profile payload the backend caches.
 */
export interface GoogleReviewDto {
  key: string
  author: string
  rating: number
  text: string
  publishedAt: string
  /** Pre-rendered by Google in Russian only — unused, see formatReviewDate. */
  relativeTime: string
  profilePhotoUrl: string | null
  language: string
  photos: string[]
  variation: unknown
  kitchenVariation: unknown
}

interface GoogleReviewsDto {
  rating: number
  totalReviews: number
  fetchedAt: string
  cached: boolean
  reviews: GoogleReviewDto[]
}

export interface GoogleReviewsData {
  averageRating: number
  totalReviews: number
  reviews: Review[]
}

export function mapReview(dto: GoogleReviewDto): Review {
  return {
    id: dto.key,
    rating: dto.rating,
    author: dto.author,
    text: dto.text,
    publishedAt: dto.publishedAt,
    imageUrl: dto.photos[0],
    language: dto.language,
  }
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/** Coarsest unit first; the first one the age reaches wins. */
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * MS_PER_DAY],
  ['month', 30 * MS_PER_DAY],
  ['week', 7 * MS_PER_DAY],
  ['day', MS_PER_DAY],
]

/**
 * Locale-aware relative date. The API's own `relativeTime` is Russian-only, so
 * it would leak Russian onto the /ro site; this recomputes from `publishedAt`.
 *
 * `numeric: 'always'` keeps the "1 месяц назад" wording Google itself uses,
 * instead of the "в прошлом месяце" phrasing 'auto' produces. Same-day reviews
 * fall back to 'auto' so they read "сегодня" rather than "0 дней назад".
 */
export function formatReviewDate(
  publishedAt: string,
  locale: string,
  now: number = Date.now()
): string {
  const diff = new Date(publishedAt).getTime() - now
  const age = Math.abs(diff)

  for (const [unit, ms] of UNITS) {
    if (age >= ms) {
      const value = Math.trunc(diff / ms)
      return new Intl.RelativeTimeFormat(locale, { numeric: 'always' }).format(
        value,
        unit
      )
    }
  }

  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
    0,
    'day'
  )
}

/** Fetch the Google reviews. Resolves to null on failure. */
export async function fetchGoogleReviews(
  signal?: AbortSignal
): Promise<GoogleReviewsData | null> {
  try {
    const { data } = await axios.get<GoogleReviewsDto>('/api/reviews/google', {
      signal,
    })
    return {
      averageRating: data.rating,
      totalReviews: data.totalReviews,
      reviews: data.reviews.map(mapReview),
    }
  } catch (err) {
    if (axios.isCancel(err)) return null
    // process.env.NODE_ENV rather than furnitureBySku's import.meta.env.DEV:
    // Vite statically replaces both, but ts-jest's CommonJS transform chokes
    // on import.meta, and this module is unit-tested.
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[reviews] /api/reviews/google failed', err)
    }
    return null
  }
}

/**
 * Process-wide cache of the in-flight request. The landing page and /reviews
 * both mount the hook; without this they would each hit the endpoint. Failures
 * are evicted so a later mount can retry.
 */
let reviewsCache: Promise<GoogleReviewsData | null> | null = null

function getGoogleReviewsCached(): Promise<GoogleReviewsData | null> {
  if (!reviewsCache) {
    reviewsCache = fetchGoogleReviews().then((data) => {
      if (data === null) reviewsCache = null
      return data
    })
  }
  return reviewsCache
}

/**
 * Load the Google reviews on mount. Returns null while loading and on failure,
 * so callers render nothing rather than an empty or placeholder section.
 */
export function useGoogleReviews(): GoogleReviewsData | null {
  const [data, setData] = useState<GoogleReviewsData | null>(null)

  useEffect(() => {
    let active = true
    getGoogleReviewsCached().then((result) => {
      if (active) setData(result)
    })
    return () => {
      active = false
    }
  }, [])

  return data
}
